import type { Block } from '@w/content-blocks'
import { pageDocument } from '@w/content-blocks-editor/doc-bindings-for-page'
import { sql } from '@w/db'
import { crdt } from '@w/crdt'
import { s } from '@w/schema'
import { isPageKind, type PageKind } from '../server/db/page-kind-policy'
import { pageRelations, pages, pageSourceColumns, type WorkspaceDb, type WorkspacePage } from './workspace-tables'
import { readPageDocument } from './page-document'

type PageReadInput = WorkspacePage & { parentId?: string | null }

/** List/tree reads never evaluate a CRDT or a virtual generated column. */
export function pageMetaColumns(table: WorkspaceDb['pages']) {
  return sql`${table.id}, ${table.title}, ${table.kind}, ${table.deletedAt}, ${table.translatable}, ${table.language}, ${table.entryId}, ${pageParentColumn(table)} AS parentId`
}

// Module scope (Zod: define schemas once): a per-call object pays construction for every query.
const pageMetaRowSchema = s.object({
  id: pages.id.zodType, title: pages.title.zodType,
  kind: s.custom<PageKind>(isPageKind), deletedAt: s.number().nullable(),
  translatable: pages.translatable.zodType, language: s.string().nullable(),
  entryId: s.string().nullable(),
  parentId: s.string().nullable(),
})

export function pageMetaSchema(spaceId: string) {
  return s.transform(pageMetaRowSchema, row => ({ ...row, spaceId, pageId: row.id }))
}

export type PageMeta = ReturnType<ReturnType<typeof pageMetaSchema>['parse']>

/** Readers already decode the document; avoid evaluating every virtual index column. */
export function pageReadColumns(table: WorkspaceDb['pages']) {
  const cached = pageReadFragments.get(table)
  if (cached) return cached
  const fragment = sql`${pageSourceColumns(table)}, ${pageParentColumn(table)} AS parentId`
  pageReadFragments.set(table, fragment)
  return fragment
}
const pageReadFragments = new WeakMap<WorkspaceDb['pages'], ReturnType<typeof sql>>()

/** Route values decode the snapshot once; the containing workspace supplies scope.
 * `version` lets the open page compare the stored row with its session without decoding `data` again. */
export function readPageRow(row: PageReadInput, spaceId: string, user?: string) {
  // `user`: the reader who may claim this Doc for editing; it authors that session's edits (crdt facade.spec
  // Author "Precedence": Session.user over actAs, so devices sharing one realm stamp their own edits).
  const doc = crdt.load(row.data, { user })
  unclaimedDocs.set(row.data, doc)
  const document = readPageDocument(doc.toJSON(), row)
  const content = authoredContent(document.content)
  return {
    id: row.id, data: row.data, version: doc.version(), updatedAt: row.updatedAt, spaceId, pageId: row.id,
    translatable: document.translatable === true,
    readonly: document.readonly === true,
    title: document.title, kind: document.content.kind,
    parentId: row.parentId ?? null, deletedAt: document.deletedAt,
    content: 'children' in content ? { ...content, children: (pageDocument(doc).content.children as unknown as Block[]) } : content,
  }
}

/** The Doc a read decoded from these exact bytes, until one editing session takes it. Its views are immutable
 * (`Doc.toJSON` returns a new projection per change), so edits never reach the PageRow built from it. */
const unclaimedDocs = new WeakMap<Uint8Array, ReturnType<typeof crdt.load>>()

/** The open page's editing session takes the Doc its read already decoded instead of decoding the same bytes
 * again; a second session for the same bytes gets a fresh load, so no session inherits another's edits. */
export function claimPageDoc(data: Uint8Array, user?: string) {
  const doc = unclaimedDocs.get(data)
  if (!doc) return crdt.load(data, { user })
  unclaimedDocs.delete(data)
  return doc
}

/** The stored row each decoded page came from, keyed by its `data` bytes: copies of a PageRow keep that reference. */
const decodedFrom = new WeakMap<Uint8Array, { source: Record<string, unknown>; page: PageRow }>()

/** The projected parent edge is one scalar; checking it needs no second object parse of the row. */
const pageParentIdSchema = s.string().nullable().optional()

/** `previous` is the reader's last result. A refetch whose stored row is unchanged (own-write echo, a pull that
 * brought nothing for this page) returns that row's page instead of decoding the same bytes again. */
export function pageReadSchema(spaceId: string, previous?: () => readonly { data: Uint8Array }[] | undefined, user?: string) {
  return s.transform(s.unknown(), value => {
    const unchanged = previous && sameSourcePage(previous(), value, spaceId)
    if (unchanged) return unchanged
    const row = pages.__selectSchema.parse(value)
    const parentId = pageParentIdSchema.parse((value as { parentId?: unknown }).parentId)
    const page = readPageRow({ ...row, parentId }, spaceId, user)
    if (value && typeof value === 'object') decodedFrom.set(page.data, { source: { ...value }, page })
    return page
  })
}

export type PageRow = ReturnType<typeof readPageRow>

function sameSourcePage(previous: readonly { data: Uint8Array }[] | undefined, value: unknown, spaceId: string): PageRow | null {
  if (!previous || !value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  for (const item of previous) {
    const decoded = item && typeof item === 'object' && item.data instanceof Uint8Array ? decodedFrom.get(item.data) : null
    if (decoded && decoded.page.spaceId === spaceId && decoded.source.id === row.id && sameRow(decoded.source, row)) return decoded.page
  }
  return null
}

function sameRow(left: Record<string, unknown>, right: Record<string, unknown>) {
  const keys = Object.keys(left)
  if (keys.length !== Object.keys(right).length) return false
  return keys.every(key => {
    const a = left[key], b = right[key]
    if (a instanceof Uint8Array && b instanceof Uint8Array) return a.byteLength === b.byteLength && a.every((byte, index) => byte === b[index])
    return Object.is(a, b)
  })
}

/** Generated form text is exposed as a page column; authored content keeps its public shape. */
function authoredContent(value: ReturnType<typeof readPageDocument>['content']) {
  if (value.kind === 'lexiconEntry' && 'forms' in value) {
    const { forms: _forms, ...content } = value
    return content
  }
  return value
}

/** Parent projection reads the replicated child edge. */
export function pageParentColumn(table: WorkspaceDb['pages']) {
  return sql`(SELECT ${pageRelations.fromPageId} FROM ${pageRelations} WHERE ${pageRelations.toPageId}=${table.id} AND ${pageRelations.type.eq('child')})`
}

const removedContentSchema = s.object({ content: s.string() })

/** Permission-scoped lookup: no readable page row, no retained bytes. */
export async function removedQuestionParts(db: WorkspaceDb, pageId: string, qid: string): Promise<Block[]> {
  // Kept by the remover's commit (commit-page-edit.ts retainRemovedParts): the CRDT's own tombstones do not survive a freeze.
  const rows = await db.query`SELECT ${db.retainedParts.content} AS content FROM ${db.retainedParts}
    WHERE ${db.retainedParts.pageId.eq(pageId)} AND ${db.retainedParts.qid.eq(qid)} AND EXISTS(SELECT 1 FROM ${db.pages} WHERE ${db.pages.id.eq(pageId)})
    ORDER BY json_extract(${db.retainedParts.content},'$.fractionalIndex'),${db.retainedParts.node}`
    .execute(removedContentSchema)
  return rows.map(row => JSON.parse(row.content) as Block)
}
